import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import { isMobile, SIZE_MD } from '../../../../hooks/useBreakPoint';

import { getGenericError } from '../../utils/errors';
import { getRewards } from '../../utils/service';
import { REWARD_PREVIEW_TYPES } from '../../config/rewards';
import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../config/tracker';
import {
  ANCHOR_DEALS,
  CATEGORY_SEARCH_PARAM,
  REWARD_ID_ROUTE_PARAM,
  STORE_ID_ROUTE_PARAM,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
} from '../../config/routes';
import { FLAGS, getDealsFilters } from '../../views/Rewards/utils';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../Button';
import LoadingBar from '../LoadingBar';
import RewardsV2Card from '../RewardsV2Card';
import SliderV3 from '../SliderV3';
import { SLIDER_TRACK_PADDING } from '../../config/config';
import ArrowRightSvg from '../SvgComponents/ArrowRight_svg';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

import './RewardsV2Shelf.scss';

export const REWARDS_V2_SHELF_TYPES = {
  HOT: REWARD_PREVIEW_TYPES.HOT,
  ENDING_SOON: REWARD_PREVIEW_TYPES.ENDING_SOON,
  SIMILAR: REWARD_PREVIEW_TYPES.SIMILAR,
  CATEGORY: REWARD_PREVIEW_TYPES.CATEGORY,
  EXCLUSIVE: REWARD_PREVIEW_TYPES.EXCLUSIVE,
  TRENDING: REWARD_PREVIEW_TYPES.TRENDING,
  FREE_STUFF: REWARD_PREVIEW_TYPES.FREE_STUFF,
};

export const DASHBOARD_V2_SHELF_TYPES = {
  SLIDER: 'slider',
  MARKETING_BANNER: 'marketingBanner',
  OFFERS_PREVIEW: 'offersPreview',
};

const {
  dashboard: {
    instantDeals: {
      viewAllButton: trackerViewAllButton,
    },
  },
} = TRACKER_IDS;

const SLIDES_PER_PAGE_DEFAULT = 6;
const SLIDES_PER_PAGE_MOBILE_MD = 4;
const SLIDES_PER_PAGE_MOBILE = 2;

const RewardsV2Shelf = ({
  type = REWARDS_V2_SHELF_TYPES.DEFAULT,
  title,
  category,
  showLoading = true,
  showOnEmptyResults = false,
  trackerProps = {},
  referenceRewardId,
  backgroundColor,
  itemCardBackgroundColor,
  defaultSlidesPerPage = SLIDES_PER_PAGE_DEFAULT,
  fullWidth = false,
  gap = '1rem',
  mobileGap = '1rem',
  advancedMode = false,
  trackPadding = SLIDER_TRACK_PADDING.SMALL,
}) => {
  const { userData } = useFirebaseAuth();
  const navigateWithSearch = useNavigateWithSearch();
  const mobile = isMobile();
  const mobileMD = isMobile([SIZE_MD]);

  const [rewards, setRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEmptyState, setIsEmptyState] = useState(false);

  const loadRewards = useCallback(async (rewardsType, categoryId, rewardId) => {
    setIsLoading(true);

    const {
      data,
      error: rewardsError,
    } = await getRewards({
      pageSize: 10,
      page: 1,
      type: rewardsType,
      ...getDealsFilters({ type, categoryId, rewardId }),
    });

    if (rewardsError) {
      setIsEmptyState(true);
      setRewards([]);
      setError(rewardsError.message || getGenericError());
      setIsLoading(false);
      return;
    }

    setRewards(data.rewards);
    setIsEmptyState(!data.rewards.length);
    setError('');
    setIsLoading(false);
  }, []);

  const handleOnClick = useCallback(() => {
    let categoryParam = FLAGS.ALL;

    if (type === REWARDS_V2_SHELF_TYPES.EXCLUSIVE) {
      categoryParam = FLAGS.IS_EXCLUSIVE;
    } else if (type === REWARDS_V2_SHELF_TYPES.HOT) {
      categoryParam = FLAGS.IS_HOT;
    } else if (type === REWARDS_V2_SHELF_TYPES.CATEGORY && category?.routeName) {
      categoryParam = category.routeName;
    } else if (type === REWARDS_V2_SHELF_TYPES.FREE_STUFF) {
      categoryParam = FLAGS.FREE_STUFF;
    } else if (type === REWARDS_V2_SHELF_TYPES.ENDING_SOON) {
      categoryParam = FLAGS.ENDING_SOON;
    }

    navigateWithSearch(ROUTE_REWARDS_V2_LIST, { [CATEGORY_SEARCH_PARAM]: categoryParam });
  }, [navigateWithSearch, type, category?.routeName]);

  const handleItemOnClick = useCallback((rewardItem) => {
    const relativePath = ROUTE_REWARDS_V2_STORE_REWARD_ID
      .replace(STORE_ID_ROUTE_PARAM, rewardItem.store?.id)
      .replace(REWARD_ID_ROUTE_PARAM, rewardItem.uid);

    navigateWithSearch(relativePath);
  }, [navigateWithSearch]);

  useEffect(() => {
    loadRewards(type, category?.uid, referenceRewardId);
  }, [type, category?.uid, referenceRewardId]);

  const emptyLoading = !showLoading && isLoading;
  const emptyState = showOnEmptyResults && isEmptyState;

  if (emptyLoading || emptyState) {
    return null;
  }

  return (
    <div
      id={`${ANCHOR_DEALS}-${type}`}
      className={classNames('RewardsV2Shelf', {
        'RewardsV2Shelf--full-width': fullWidth,
      })}
    >
      <div className="RewardsV2Shelf__container">
        <div className={classNames('RewardsV2Shelf__container-top', trackPadding, {
          'RewardsV2Shelf--no-margin': fullWidth,
        })}
        >
          {title && (
            <h2 className="RewardsV2Shelf__container-top-text">{title}</h2>
          )}
          <Button
            size={BUTTON_SIZE.LARGE}
            type={BUTTON_TYPE.LINK_QUINARY}
            onClick={handleOnClick}
            className="RewardsV2Shelf__container-top-cta"
            trackerProps={{
              [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerViewAllButton),
              [POSTHOG_CAPTURE_ATTRIBUTES.SECTION]: type,
              ...(type === REWARDS_V2_SHELF_TYPES.CATEGORY && {
                [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY_ID]: category?.uid,
              }),
            }}
          >
            <ArrowRightSvg color="var(--trashie-ui-faded-black-90)" />
          </Button>
        </div>
        <div className="RewardsV2Shelf__container-content">
          {isLoading ? (
            <LoadingBar className="RewardsV2Shelf__container-loader" />
          ) : (
            error ||
            (rewards.length === 0 && 'No Deals available at the moment') ||
            (
              <SliderV3
                id={`rewards-v2-shelf-${type}`}
                slidesPerPage={mobileMD
                  ? SLIDES_PER_PAGE_MOBILE_MD
                  : (mobile && SLIDES_PER_PAGE_MOBILE) || defaultSlidesPerPage}
                backgroundColor={backgroundColor}
                itemCardBackgroundColor={itemCardBackgroundColor}
                gap={gap}
                mobileGap={mobileGap}
                trackPadding={trackPadding}
                advancedMode={advancedMode}
                slides={rewards.map((rewardItem, index) => ({
                  key: `deal-preview-${rewardItem.uid}`,
                  slide: (
                    <RewardsV2Card
                      key={`slide-deal-${rewardItem.uid}`}
                      index={index}
                      rewardItem={rewardItem}
                      onClick={handleItemOnClick}
                      trackerProps={{
                        [POSTHOG_CAPTURE_ATTRIBUTES.SECTION]: type,
                        ...(type === REWARDS_V2_SHELF_TYPES.CATEGORY && {
                          [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY_ID]: category?.uid,
                        }),
                        ...trackerProps,
                      }}
                      backgroundColor={itemCardBackgroundColor}
                      activeSubscription={userData?.activeSubscription}
                      cashbackMultiplierValue={userData?.cashbackMultiplierValue}
                    />
                  ),
                }))}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsV2Shelf;
